/**
 * The sequencer (04 §5): plays a compiled song on the synth's voices, scheduled ahead on the
 * WebAudio clock (clock.ts). One song at a time; changing song fades the old one out under the
 * new one's start.
 */

import type { Bus } from '../bus';
import type { Synth } from '../synth';
import { collectDue, type Cursor, LEAD_IN, LOOKAHEAD, onceEnds, stepSeconds } from './clock';
import { voicesFor } from './instruments';
import type { Compiled } from './types';

const FADE_IN = 0.04;
const FADE_OUT = 0.9;
/** A jingle is over in seconds: what was playing gets out of its way at once. */
const FADE_OUT_QUICK = 0.12;

interface Run {
  readonly name: string;
  readonly song: Compiled;
  readonly out: GainNode;
  readonly start: number;
  readonly stepSec: number;
  readonly cursor: Cursor;
}

export class MusicPlayer {
  private run: Run | null = null;
  /** Songs fading out, and when to let go of them. */
  private fading: { out: GainNode; until: number }[] = [];

  constructor(
    private readonly bus: Bus,
    private readonly synth: Synth,
  ) {}

  /** The song playing, or null (none, or a once-only song has finished). */
  get playing(): string | null {
    return this.run?.name ?? null;
  }

  /** Start `song` from the top under `name`, fading out whatever was playing. Same name: nothing changes. */
  play(name: string, song: Compiled): void {
    if (this.run?.name === name) return;
    this.fadeOut(song.loop ? FADE_OUT : FADE_OUT_QUICK);
    const now = this.bus.now;
    const out = this.bus.ctx.createGain();
    out.gain.setValueAtTime(0, now);
    out.gain.linearRampToValueAtTime(1, now + FADE_IN);
    out.connect(this.bus.music);
    this.run = {
      name,
      song,
      out,
      start: now + LEAD_IN,
      stepSec: stepSeconds(song.bpm),
      cursor: { index: 0, pass: 0 },
    };
    this.tick();
  }

  /** `quick`: something short is about to play in its place (a jingle from a file). */
  stop(quick = false): void {
    this.fadeOut(quick ? FADE_OUT_QUICK : FADE_OUT * 0.6);
  }

  /** Hand WebAudio every note due in the look-ahead window. Call every `TICK_MS`. */
  tick(): void {
    const now = this.bus.now;
    this.fading = this.fading.filter((f) => {
      if (now < f.until) return true;
      f.out.disconnect();
      return false;
    });
    const r = this.run;
    if (!r) return;
    for (const due of collectDue(r.song, r.cursor, r.start, r.stepSec, now, now + LOOKAHEAD)) {
      for (const v of voicesFor(due.event, r.stepSec, r.song.mix)) this.synth.play(v, due.time, r.out);
    }
    if (!r.song.loop && now > onceEnds(r.song, r.start, r.stepSec)) {
      r.out.disconnect();
      this.run = null;
    }
  }

  private fadeOut(seconds: number): void {
    const r = this.run;
    if (!r) return;
    this.run = null;
    const g = r.out.gain;
    const now = this.bus.now;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + seconds);
    this.fading.push({ out: r.out, until: now + seconds + 0.1 });
  }
}
